import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClientFilter } from '@/hooks/useClientFilter';

interface SLAMetric {
  category: string;
  target: number;
  current: number;
  total: number;
  onTime: number;
  breached: number;
}

export const useSLAPerformance = () => {
  const { getClientFilter } = useClientFilter();

  return useQuery({
    queryKey: ['sla-performance', getClientFilter()],
    queryFn: async (): Promise<SLAMetric[]> => {
      const metrics: SLAMetric[] = [];
      const clientId = getClientFilter();

      const withClient = (query: any, field = 'client_id') => {
        return clientId ? query.eq(field, clientId) : query;
      };
      
      const { data: solicitacoes } = await withClient(
        supabase
          .from('solicitacoes')
          .select(`
            id, tipo, status, data_limite_resolucao, data_resolucao, data_fechamento,
            categoria:categorias_servico(nome),
            sla:slas(nome, tempo_resolucao_min)
          `)
          .not('data_limite_resolucao', 'is', null)
      );

      const { data: incidentes } = await withClient(
        supabase
          .from('incidentes')
          .select('id, tipo, status, data_limite_resolucao, data_abertura')
          .not('data_limite_resolucao', 'is', null)
      );

      const { data: mudancas } = await withClient(
        supabase
          .from('mudancas')
          .select('id, tipo, status, data_limite_resolucao, data_abertura')
          .not('data_limite_resolucao', 'is', null)
      );

      const { data: problemas } = await withClient(
        supabase
          .from('problemas')
          .select('id, tipo, status, data_limite_resolucao, data_abertura')
          .not('data_limite_resolucao', 'is', null)
      );

      const tiposConfig = [
        { 
          key: 'requisicao', label: 'Requisições', 
          data: solicitacoes?.filter(s => s.tipo === 'requisicao' || s.tipo === 'solicitacao') || [],
          resolvedStatuses: ['resolvida', 'fechada']
        },
        { 
          key: 'incidente', label: 'Incidentes', 
          data: incidentes || [],
          resolvedStatuses: ['resolvido', 'fechado']
        },
        { 
          key: 'mudanca', label: 'Mudanças', 
          data: mudancas || [],
          resolvedStatuses: ['implementada', 'fechada', 'concluida']
        },
        { 
          key: 'problema', label: 'Problemas', 
          data: problemas || [],
          resolvedStatuses: ['resolvido', 'fechado']
        }
      ];

      tiposConfig.forEach(config => {
        const tickets = config.data;
        const total = tickets.length;
        
        if (total === 0) {
          metrics.push({ category: config.label, target: 95, current: 0, total: 0, onTime: 0, breached: 0 });
          return;
        }

        let onTime = 0;
        let breached = 0;

        tickets.forEach(ticket => {
          const dataLimite = new Date(ticket.data_limite_resolucao);
          const agora = new Date();

          if (config.resolvedStatuses.includes(ticket.status)) {
            onTime++;
          } else {
            if (agora <= dataLimite) {
              onTime++;
            } else {
              breached++;
            }
          }
        });

        const currentPercentage = total > 0 ? Math.round((onTime / total) * 100) : 0;
        metrics.push({ category: config.label, target: 95, current: currentPercentage, total, onTime, breached });
      });

      const totalGeral = metrics.reduce((acc, m) => acc + m.total, 0);
      const onTimeGeral = metrics.reduce((acc, m) => acc + m.onTime, 0);
      const breachedGeral = metrics.reduce((acc, m) => acc + m.breached, 0);
      
      if (totalGeral > 0) {
        metrics.unshift({
          category: 'SLA Geral',
          target: 95,
          current: Math.round((onTimeGeral / totalGeral) * 100),
          total: totalGeral,
          onTime: onTimeGeral,
          breached: breachedGeral
        });
      }

      return metrics;
    },
    refetchInterval: 5 * 60 * 1000,
  });
};
